export type TutorialTextboxSide = 'bottom' | 'left' | 'right' | 'top' | 'center' | 'smoothprojactions'

export type RectLike = Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left' | 'width' | 'height'>

type Size = {
  width: number
  height: number
}

type Viewport = Size

const TEXTBOX_MARGIN = 15
const VIEWPORT_PADDING = 12
const MIN_TEXTBOX_WIDTH = 320
const MAX_TEXTBOX_WIDTH = 640

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export const getTutorialTextboxWidth = (targetRect: RectLike, viewport: Viewport) => {
  const availableWidth = Math.max(viewport.width - VIEWPORT_PADDING * 2, 0)
  const naturalWidth = clamp(targetRect.width, MIN_TEXTBOX_WIDTH, MAX_TEXTBOX_WIDTH)

  return Math.min(naturalWidth, availableWidth)
}

const fitsBelow = (targetRect: RectLike, textboxSize: Size, viewport: Viewport) =>
  targetRect.bottom + TEXTBOX_MARGIN + textboxSize.height <= viewport.height - VIEWPORT_PADDING

const fitsAbove = (targetRect: RectLike, textboxSize: Size) =>
  targetRect.top - TEXTBOX_MARGIN - textboxSize.height >= VIEWPORT_PADDING

const fitsRight = (targetRect: RectLike, textboxSize: Size, viewport: Viewport) =>
  targetRect.right + TEXTBOX_MARGIN + textboxSize.width <= viewport.width - VIEWPORT_PADDING

const fitsLeft = (targetRect: RectLike, textboxSize: Size) =>
  targetRect.left - TEXTBOX_MARGIN - textboxSize.width >= VIEWPORT_PADDING

export const getTutorialTextboxPosition = (
  targetRect: RectLike,
  preferredSide: TutorialTextboxSide,
  textboxSize: Size,
  viewport: Viewport
) => {
  let side = preferredSide

  if (side === 'bottom' && !fitsBelow(targetRect, textboxSize, viewport) && fitsAbove(targetRect, textboxSize)) {
    side = 'top'
  } else if (side === 'top' && !fitsAbove(targetRect, textboxSize) && fitsBelow(targetRect, textboxSize, viewport)) {
    side = 'bottom'
  } else if (side === 'left' && !fitsLeft(targetRect, textboxSize) && fitsRight(targetRect, textboxSize, viewport)) {
    side = 'right'
  } else if (side === 'right' && !fitsRight(targetRect, textboxSize, viewport) && fitsLeft(targetRect, textboxSize)) {
    side = 'left'
  }

  let top = targetRect.top
  let left = targetRect.left

  if (side === 'bottom') {
    top = targetRect.bottom + TEXTBOX_MARGIN
  } else if (side === 'top') {
    top = targetRect.top - textboxSize.height - TEXTBOX_MARGIN
  } else if (side === 'left') {
    left = targetRect.left - textboxSize.width - TEXTBOX_MARGIN
  } else if (side === 'right') {
    left = targetRect.right + TEXTBOX_MARGIN
  } else if (side === 'center') {
    top = targetRect.top + targetRect.height / 2 - textboxSize.height / 2
    left = targetRect.left + targetRect.width / 2 - textboxSize.width / 2
  } else if (side === 'smoothprojactions') {
    top = targetRect.bottom + TEXTBOX_MARGIN
    left = targetRect.right - textboxSize.width
  }

  return {
    top: clamp(top, VIEWPORT_PADDING, Math.max(viewport.height - textboxSize.height - VIEWPORT_PADDING, VIEWPORT_PADDING)),
    left: clamp(left, VIEWPORT_PADDING, Math.max(viewport.width - textboxSize.width - VIEWPORT_PADDING, VIEWPORT_PADDING)),
  }
}
